import { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import RHFWrapper from "@/components/RHFWrapper";
import { useAuth } from "@/contexts/use-auth";
import { useGetData } from "@/hooks/useGetData";
import { DashboardLayout } from "@/pages/Dashboard";
import LoaderWrapper from "@/pages/LoaderWrapper";
import { patientV2LoaderWrapper } from "../shared/PatientV2CircularLoader";
import AxiosInstance from "@/utils/AxiosInstance";
import { formatDate, getPromise, getPromiseToast } from "@/utils/helpers";
import type { PatientV2 } from "../types";
import { ScheduleTelehealthDialog } from "./ScheduleTelehealthDialog";
import { TelehealthBookedDialog } from "./TelehealthBookedDialog";
import {
  TcmAppointmentReasonDialog,
  type TcmAppointmentAction,
} from "./TcmAppointmentReasonDialog";
import { TcmAutoStatus, TcmPlanDetailsCard } from "./TcmPlanDetailsCard";
import {
  TcmTelehealthEncounters,
  type TelehealthAppointment,
} from "./TcmTelehealthEncounters";
import {
  extractTcmPlan,
  tcmCreatePayload,
  tcmPlanFormValues,
  tcmUpdatePayload,
} from "./tcmApi";
import { tcmPlanInitialValues, tcmPlanSchema } from "./tcmPlanDummy";

function patientName(patient: PatientV2 | null) {
  return (
    [patient?.firstName, patient?.middleName, patient?.lastName].filter(Boolean).join(" ") ||
    "Patient"
  );
}

function SaveTcmPlanButton({ disabled }: { disabled?: boolean }) {
  const { formState } = useFormContext();
  return (
    <Button type="submit" disabled={disabled || formState.isSubmitting}>
      <Check className="h-4 w-4" />
      Save Changes
    </Button>
  );
}

function anthroPatientId(patient: PatientV2 | null) {
  const code = String(patient?.patientId ?? "").trim();
  if (!code || /^[a-f0-9]{24}$/i.test(code)) return "";
  return code;
}

export function TcmPlanPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { patientId = "", tcmPlanId } = useParams();
  const { state } = useLocation();
  const statePatient = (state as { patientData?: PatientV2 } | null)?.patientData;
  const { apiData } = useGetData<{ data?: PatientV2 }>(
    `/patients/${patientId}`,
    {},
    false,
    !!patientId && !statePatient,
  );
  const patient = statePatient ?? apiData?.data ?? null;
  const isEdit = Boolean(tcmPlanId);
  const isCaretaker = String(user?.role ?? "").toLowerCase() === "caretaker";
  const { apiData: tcmPlanData, loading: tcmPlanLoading, err: tcmPlanErr } = useGetData(
    `/tcm-plans/${tcmPlanId}`,
    {},
    false,
    isEdit && !!tcmPlanId,
  );
  const [formValues, setFormValues] = useState(tcmPlanInitialValues);
  const [planReady, setPlanReady] = useState(!isEdit);
  const [appointments, setAppointments] = useState<TelehealthAppointment[]>([]);
  const [telehealthRefreshKey, setTelehealthRefreshKey] = useState(0);
  useEffect(() => {
    if (!isEdit) return;
    const plan = extractTcmPlan(tcmPlanData);
    if (!plan) return;
    setFormValues(tcmPlanFormValues(plan));
    setPlanReady(true);
  }, [isEdit, tcmPlanData]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [bookedOpen, setBookedOpen] = useState(false);
  const [bookedMeetingLink, setBookedMeetingLink] = useState("");
  const [appointmentAction, setAppointmentAction] = useState<{
    type: TcmAppointmentAction;
    encounter: TelehealthAppointment;
  } | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<{
    appointmentId: string;
    reason: string;
  } | null>(null);

  const loadedPlan = useMemo(() => (isEdit ? extractTcmPlan(tcmPlanData) : null), [isEdit, tcmPlanData]);

  const hasUpcomingAppointment = useMemo(
    () => appointments.some((appointment) => appointment.status === "scheduled"),
    [appointments],
  );

  const backToPatient = () => {
    if (!patientId) {
      navigate("/patients");
      return;
    }
    navigate(`/patients/${patientId}?tab=care-plan&subtab=tcm-plan`);
  };

  const waitingForPlan = isEdit && !planReady;

  return (
    <DashboardLayout activePage="/patients">
      {waitingForPlan ? (
        <LoaderWrapper
          loading={tcmPlanLoading || (!tcmPlanErr && !tcmPlanData)}
          error={tcmPlanErr}
          customLoaderComponent={patientV2LoaderWrapper()}
        >
          <div className="w-full p-4 text-center text-muted-foreground">
            Unable to load this TCM plan.
          </div>
        </LoaderWrapper>
      ) : (
      <RHFWrapper
        className="space-y-4"
        initialValues={formValues}
        isValidate
        validateSchema={tcmPlanSchema}
        onSubmit={async (values) => {
          if (isEdit && !planReady) return;
          const { pr, resolveFn, rejectFn } = getPromise();
          getPromiseToast(pr);
          try {
            if (isEdit && tcmPlanId) {
              await AxiosInstance().put(`/tcm-plans/${tcmPlanId}`, tcmUpdatePayload(values));
              resolveFn("TCM plan updated");
            } else {
              await AxiosInstance().post("/tcm-plans", tcmCreatePayload(patientId, values));
              resolveFn("TCM plan created");
            }
            backToPatient();
          } catch (error) {
            rejectFn(error);
          }
        }}
      >
        <TcmAutoStatus
          hasUpcomingAppointment={hasUpcomingAppointment}
          isCaretaker={isCaretaker}
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold text-foreground">
              {patientName(patient)}
              {anthroPatientId(patient) ? (
                <span className="ml-2 text-base font-medium text-muted-foreground">
                  ({anthroPatientId(patient)})
                </span>
              ) : null}
            </h1>
            {isEdit ? (
              <p className="mt-1 text-xs text-muted-foreground">
                <span className="font-semibold uppercase tracking-wide">Created:</span>{" "}
                <span className="text-foreground">
                  {formatDate(loadedPlan?.createdAt) || "—"}
                </span>
                <span className="mx-2">·</span>
                <span className="font-semibold uppercase tracking-wide">Created By:</span>{" "}
                <span className="text-foreground">{loadedPlan?.createdBy || "—"}</span>
              </p>
            ) : null}
          </div>
          <SaveTcmPlanButton disabled={isEdit && (tcmPlanLoading || !planReady)} />
        </div>

        <TcmPlanDetailsCard isCaretaker={isCaretaker} />
        <TcmTelehealthEncounters
          enabled={isEdit}
          tcmPlanId={tcmPlanId ?? ""}
          displayName={patientName(patient)}
          refreshKey={telehealthRefreshKey}
          onRows={setAppointments}
          onSchedule={() => setScheduleOpen(true)}
          onReschedule={(encounter) =>
            setAppointmentAction({ type: "reschedule", encounter })
          }
          onCancel={(encounter) => setAppointmentAction({ type: "cancel", encounter })}
        />
      </RHFWrapper>
      )}

      <TcmAppointmentReasonDialog
        action={appointmentAction?.type ?? null}
        onOpenChange={(open) => {
          if (!open) setAppointmentAction(null);
        }}
        onConfirm={(reason) => {
          const current = appointmentAction;
          if (!current) return;
          if (current.type === "reschedule") {
            setRescheduleTarget({ appointmentId: current.encounter.id, reason });
            return;
          }
          const { pr, resolveFn, rejectFn } = getPromise();
          getPromiseToast(pr);
          AxiosInstance()
            .post(`/telehealth/appointments/${current.encounter.id}/cancel`, { reason })
            .then(() => {
              resolveFn("Appointment cancelled");
              setTelehealthRefreshKey((key) => key + 1);
            })
            .catch((error) => rejectFn(error));
        }}
      />
      <ScheduleTelehealthDialog
        open={scheduleOpen || !!rescheduleTarget}
        onOpenChange={(open) => {
          if (open) return;
          setScheduleOpen(false);
          setRescheduleTarget(null);
        }}
        patient={patient}
        patientId={patient?._id || patientId}
        tcmPlanId={tcmPlanId ?? ""}
        rescheduleAppointmentId={rescheduleTarget?.appointmentId}
        rescheduleReason={rescheduleTarget?.reason}
        onScheduled={({ meetingLink }) => {
          setTelehealthRefreshKey((current) => current + 1);
          if (rescheduleTarget) return;
          setBookedMeetingLink(meetingLink);
          setBookedOpen(true);
        }}
      />
      <TelehealthBookedDialog
        open={bookedOpen}
        onOpenChange={(open) => {
          setBookedOpen(open);
          if (!open) setBookedMeetingLink("");
        }}
        meetingLink={bookedMeetingLink}
      />
    </DashboardLayout>
  );
}
